// src/components/Billing/FreePlanHomeOverlay.js
// "Unlock More Tabs" pricing panel — the single upgrade menu used everywhere
// a free-plan tab limit is hit: floated inline over the blurred Home tab, or
// as a dismissible modal wherever else an upgrade nudge used to be a plain
// alert(). Mirrors the pricing section design in LiveShopCreation so the
// upgrade pitch stays visually consistent across the whole app.

import React from 'react';
import { X } from 'lucide-react';
import { PLAN_ORDER, PLANS, PLAN_PERMISSIONS } from '../../utils/billingTiers';

const FreePlanHomeOverlay = ({ theme, onSelectPlan, asModal = false, onClose }) => {
  const accent = theme?.colors?.accent || '#800000';
  const text = theme?.colors?.text || '#ffffff';

  const paidPlans = PLAN_ORDER.filter(key => key !== 'free');

  const card = (
    <div
      style={{
        position: 'relative',
        animation: 'freePlanEaseUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) both',
        background: 'rgba(15,15,15,0.92)',
        border: `1px solid ${accent}40`,
        borderRadius: 'clamp(14px, 2.5vw, 20px)',
        padding: 'clamp(1.25rem, 3vw, 2rem)',
        maxWidth: '640px',
        width: '100%',
        boxShadow: `0 20px 60px rgba(0,0,0,0.55), 0 0 30px ${accent}25`,
        backdropFilter: 'blur(6px)',
      }}
    >
      {asModal && onClose && (
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: '0.75rem', right: '0.75rem',
            background: 'transparent', border: 'none', color: text,
            cursor: 'pointer', padding: '0.25rem', opacity: 0.6, display: 'flex',
          }}
        >
          <X size={18} />
        </button>
      )}

      <div style={{ textAlign: 'center', marginBottom: 'clamp(1rem, 2.5vw, 1.5rem)' }}>
        <h2 style={{ fontSize: 'clamp(1.1rem, 3vw, 1.6rem)', fontWeight: 800, color: accent, margin: 0 }}>
          Unlock More Tabs
        </h2>
        <p style={{ fontSize: 'clamp(0.75rem, 1.8vw, 0.9rem)', color: `${text}99`, margin: '0.4rem 0 0', lineHeight: 1.5 }}>
          Upgrade to publish more tabs for visitors.
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(120px, 22vw, 160px), 1fr))',
          gap: 'clamp(0.5rem, 1.5vw, 0.85rem)',
        }}
      >
        {paidPlans.map(key => {
          const perms = PLAN_PERMISSIONS[key];
          const plan = PLANS[key];
          return (
            <div
              key={key}
              onClick={() => onSelectPlan(key)}
              role="button"
              tabIndex={0}
              style={{
                borderRadius: 'clamp(10px, 2vw, 14px)',
                border: `2px solid rgba(255,255,255,0.1)`,
                background: 'rgba(0,0,0,0.35)',
                padding: 'clamp(0.65rem, 1.8vw, 0.9rem) clamp(0.55rem, 1.6vw, 0.8rem)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.2rem' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', border: `2px solid ${plan.color}` }} />
                <span style={{ fontWeight: 800, fontSize: 'clamp(0.7rem, 2vw, 0.85rem)', color: plan.color }}>{plan.label}</span>
              </div>
              <div style={{ fontWeight: 900, fontSize: 'clamp(1rem, 3vw, 1.3rem)', color: 'white', lineHeight: 1 }}>${plan.price}</div>
              <div style={{ fontSize: 'clamp(0.55rem, 1.4vw, 0.62rem)', color: `${plan.color}cc`, fontWeight: 600, marginBottom: '0.5rem' }}>one-time</div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                <li style={{ fontSize: 'clamp(0.58rem, 1.5vw, 0.66rem)', color: 'rgba(255,255,255,0.8)' }}>
                  📦 {perms.maxVisible} visible cards
                </li>
                <li style={{ fontSize: 'clamp(0.58rem, 1.5vw, 0.66rem)', color: 'rgba(255,255,255,0.8)' }}>
                  🗂️ {perms.maxTabs} tabs
                </li>
              </ul>
            </div>
          );
        })}
      </div>

      <p style={{ textAlign: 'center', fontSize: 'clamp(0.6rem, 1.5vw, 0.7rem)', color: `${text}55`, marginTop: 'clamp(0.75rem, 2vw, 1rem)', marginBottom: 0 }}>
        Tap any plan to continue to payment.
      </p>
    </div>
  );

  return (
    <div
      onClick={asModal ? onClose : undefined}
      style={asModal ? {
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        background: 'rgba(0,0,0,0.75)',
        backdropFilter: 'blur(8px)',
        zIndex: 10000,
      } : {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '1.5rem',
        // Clears the fixed header (~78px mobile / ~140px desktop) with extra
        // breathing room, instead of sitting flush against the very top.
        paddingTop: 'clamp(130px, 20vw, 210px)',
        zIndex: 5,
      }}
    >
      <style>{`
        @keyframes freePlanEaseUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      {asModal ? <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: '640px' }}>{card}</div> : card}
    </div>
  );
};

export default FreePlanHomeOverlay;
